import React from 'react';

import { UseFormReturn } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { PricingPlanPeriodAdjustmentFormSchemaValues } from 'src/features/pricingPlan/utils/formSchema';
import { DAY_OF_WEEK, PAYMENT_TYPE } from 'src/features/pricingPlan/utils/types';
import {
    DAY_OF_WEEK_TRANSLATIONS_MAP,
    PAYMENT_TYPE_TRANSLATION_MAP,
} from 'src/features/pricingPlan/utils/constants';
import { pricingPlanMessages } from 'src/features/pricingPlan/utils/messages';
import SelectFormField from 'src/registry/components/forms/SelectFormField';
import CalendarWithTime from 'src/registry/components/inputs/CalendarWithTime';
import { Form, FormControl, FormControlStack, FormItem, FormLabel, FormMessage, FormField } from 'src/registry/ui/Form';
import { Input } from 'src/registry/ui/Input';

type AdjustmentFormProps = {
    form: UseFormReturn<PricingPlanPeriodAdjustmentFormSchemaValues>;
    onSubmit: (values: PricingPlanPeriodAdjustmentFormSchemaValues) => void;
};

function AdjustmentForm({ onSubmit, form }: AdjustmentFormProps) {
    const { formatMessage } = useIntl();

    const [displayAdjustmentAmount, setDisplayAdjustmentAmount] = React.useState('');

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
            <FormField
                control={form.control}
                name="valid_from"
                render={({ field }) => {
                    return (
                        <FormItem variant="dialog">
                            <FormLabel variant="dialog" required>
                                {formatMessage(pricingPlanMessages.validFrom)}
                            </FormLabel>
                            <FormControlStack>
                                <FormControl>
                                    <CalendarWithTime
                                        onChange={field.onChange}
                                        dateISO={field.value}
                                        placeholder={`${formatMessage(pricingPlanMessages.validFrom)}...`}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormControlStack>
                        </FormItem>
                    );
                }}
            />
            <FormField
                control={form.control}
                name="valid_to"
                render={({ field }) => {
                    return (
                        <FormItem variant="dialog">
                            <FormLabel variant="dialog">{formatMessage(pricingPlanMessages.validTo)}</FormLabel>
                            <FormControlStack>
                                <FormControl>
                                    <CalendarWithTime
                                        onChange={field.onChange}
                                        dateISO={field.value ?? undefined}
                                        placeholder={`${formatMessage(pricingPlanMessages.validTo)}...`}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormControlStack>
                        </FormItem>
                    );
                }}
            />

            <SelectFormField<PricingPlanPeriodAdjustmentFormSchemaValues>
                form={form}
                name="payment_type"
                label={formatMessage(pricingPlanMessages.paymentType)}
                options={Object.values(PAYMENT_TYPE)}
                optionTranslationMap={PAYMENT_TYPE_TRANSLATION_MAP}
            />

            <SelectFormField<PricingPlanPeriodAdjustmentFormSchemaValues>
                required={false}
                form={form}
                name="day_of_week"
                label={formatMessage(pricingPlanMessages.day)}
                options={Object.values(DAY_OF_WEEK)}
                optionTranslationMap={DAY_OF_WEEK_TRANSLATIONS_MAP}
            />
            <FormField
                control={form.control}
                name="from_time"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog">
                            <FormattedMessage {...pricingPlanMessages.fromHour} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Input type="time" {...field} value={field.value ?? ''} />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <FormField
                control={form.control}
                name="to_time"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog">
                            <FormattedMessage {...pricingPlanMessages.toHour} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Input type="time" {...field} value={field.value ?? ''} />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <FormField
                control={form.control}
                name="adjustment_percent"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog">
                            <FormattedMessage {...pricingPlanMessages.adjustmentPercent} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Input
                                    type="text"
                                    inputMode="decimal"
                                    placeholder={`${formatMessage(pricingPlanMessages.adjustmentPercent)}...`}
                                    value={field.value ?? ''}
                                    onChange={(e) => {
                                        const inputValue = e.target.value;
                                        if (inputValue === '' || /^-?\d{0,20}(?:\.\d{0,4})?$/.test(inputValue)) {
                                            field.onChange(inputValue);
                                        }
                                    }}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />

            <FormField
                control={form.control}
                name="adjustment_amount"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog">
                            <FormattedMessage {...pricingPlanMessages.adjustmentAmount} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Input
                                    type="text"
                                    inputMode="decimal"
                                    placeholder={`${formatMessage(pricingPlanMessages.adjustmentAmount)}...`}
                                    value={
                                        displayAdjustmentAmount !== ''
                                            ? displayAdjustmentAmount
                                            : field.value != null
                                              ? (field.value / 100).toFixed(2)
                                              : ''
                                    }
                                    onChange={(e) => {
                                        const inputValue = e.target.value;

                                        if (!/^\d*\.?\d{0,2}$/.test(inputValue)) return;

                                        setDisplayAdjustmentAmount(inputValue);

                                        const numeric = parseFloat(inputValue);
                                        if (!isNaN(numeric)) {
                                            field.onChange(Math.round(numeric * 100));
                                        } else {
                                            field.onChange(undefined);
                                        }
                                    }}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
        </Form>
    );
}

export default AdjustmentForm;
